import { useSupabase } from "@/lib/supabase/client";
import { useSession } from "@/lib/auth-client";
import { useEffect, useRef, useState, useCallback } from "react";
import { RealtimeChannel } from "@supabase/supabase-js";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface RealtimeEventHandler<T = any> {
  event: string;
  handler: ({ event, payload, type }: { event: string; payload: T; type: string }) => void;
}

export interface RealtimeSubscriptionConfig {
  channelName: string;
  events: RealtimeEventHandler[];
  isPrivate?: boolean;
  onStatusChange?: (status: string, error?: Error) => void;
}

export interface UseSupabaseRealtimeOptions {
  config: RealtimeSubscriptionConfig;
  enabled?: boolean;
}

export const useSupabaseRealtime = ({ config, enabled = true }: UseSupabaseRealtimeOptions) => {
  const session = useSession();
  const supabase = useSupabase();
  const [authenticated, setAuthenticated] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const channelRef = useRef<RealtimeChannel | null>(null);

  const authenticate = useCallback(async () => {
    if (session.data?.supabaseAccessToken) {
      try {
        await supabase.realtime.setAuth(session.data.supabaseAccessToken);
        setAuthenticated(true);
        setError(null);
      } catch (err) {
        setError(err as Error);
        setAuthenticated(false);
      }
    } else {
      setAuthenticated(false);
    }
  }, [session.data?.supabaseAccessToken, supabase]);

  useEffect(() => {
    authenticate();
  }, [authenticate]);

  useEffect(() => {
    if (!enabled || !authenticated || channelRef.current) {
      console.log(
        "not subscribing to channel",
        `enabled: ${enabled}, authenticated: ${authenticated}, channelRef.current: ${channelRef.current}`
      );
      return;
    }

    try {
      const channel = supabase.channel(config.channelName, {
        config: {
          private: config.isPrivate ?? false,
        },
      });

      // Subscribe to all configured events
      config.events.forEach(({ event, handler }) => {
        channel.on("broadcast", { event }, handler);
      });

      // Handle subscription status changes
      channel.subscribe((status, err) => {
        console.log("channel subscribed", status, err);
        setIsConnected(status === "SUBSCRIBED");
        if (err) {
          setError(err as Error);
        } else {
          setError(null);
        }
        config.onStatusChange?.(status, err);
      });

      channelRef.current = channel;
    } catch (err) {
      setError(err as Error);
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
        setIsConnected(false);
      }
    };
  }, [enabled, authenticated, config.channelName, config.isPrivate, supabase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, []);

  return {
    isConnected,
    isAuthenticated: authenticated,
    error,
    channel: channelRef.current,
  };
};

// Convenience hook for broadcast events (most common use case)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useSupabaseBroadcast = <T = any>(
  channelName: string,
  events: RealtimeEventHandler<T>[],
  options: {
    isPrivate?: boolean;
    enabled?: boolean;
    onStatusChange?: (status: string, error?: Error) => void;
  } = {}
) => {
  return useSupabaseRealtime({
    config: {
      channelName,
      events,
      isPrivate: options.isPrivate,
      onStatusChange: options.onStatusChange,
    },
    enabled: options.enabled,
  });
};
